// Turning module plugins into the shell's two-level navigation: a rail of sections, and the pages
// of whichever section you are in.
//
// Pure functions with no React in them, because this is where navigation quietly goes wrong — a
// section that disappears for a role that should see it, or a detail page that highlights the
// wrong section.
import { enabledPlugins, type PluginNavItem } from "./plugin";

export interface NavSection {
  /** The `group` name plugins contribute items to, e.g. "Accounting". */
  group: string;
  /** Icon for the rail, from the host app's section list. */
  icon: string;
  items: PluginNavItem[];
}

export interface SectionMeta {
  /** Sort key; the host app decides the order of sections. */
  rank: (group: string) => number;
  icon: (group: string) => string;
}

/**
 * The sections this user can see: every enabled plugin's nav groups merged, each group's items
 * filtered by permission, groups with nothing left dropped.
 */
export function navSections(
  modules: string[] | undefined,
  can: (perm: string) => boolean,
  operator: boolean,
  meta: SectionMeta,
): NavSection[] {
  const byGroup = new Map<string, PluginNavItem[]>();
  for (const p of enabledPlugins(modules)) {
    for (const g of p.nav ?? []) {
      byGroup.set(g.group, [...(byGroup.get(g.group) ?? []), ...g.items]);
    }
  }
  return [...byGroup.entries()]
    .map(([group, items]) => ({
      group,
      icon: meta.icon(group),
      // An operator-only item is for operators of the installation, whatever their permissions.
      items: items.filter((it) => (it.operator ? operator : !it.perm || can(it.perm))),
    }))
    .filter((s) => s.items.length > 0)
    .sort((a, b) => meta.rank(a.group) - meta.rank(b.group));
}

/**
 * Which section a URL belongs to: the nav item whose path is the longest prefix of it, so
 * `/invoices/123/edit` lands on Finance's Invoices rather than on whatever else starts with `/i`.
 * `null` when the page is under no section at all (Overview, My account).
 */
export function sectionForPath(sections: NavSection[], path: string): string | null {
  let best: { group: string; len: number } | null = null;
  for (const s of sections) {
    for (const it of s.items) {
      // "/" would be a prefix of everything; Overview is not a section.
      if (it.to === "/") continue;
      if (path === it.to || path.startsWith(`${it.to}/`)) {
        if (!best || it.to.length > best.len) best = { group: s.group, len: it.to.length };
      }
    }
  }
  return best?.group ?? null;
}
